import { errorMessage } from '../services/apiError';
import { paymentService } from '../services/paymentService';
import { Project } from '../services/projectService';

interface HandlerDependencies {
  setError: (error: string | null) => void;
  setSelectedProjectIds: (ids: string[] | ((prev: string[]) => string[])) => void;
  setCurrentPage: (page: number) => void;
  setLedgerPage: (page: number) => void;
  setExpandedProjects: (
    projects: Set<string> | ((prev: Set<string>) => Set<string>)
  ) => void;
  selectedProjectIds: string[];
  allProjects: Project[];
  projectGroupIds: string[];
}

const handleDownloadReceipt = (deps: HandlerDependencies) => async (paymentId: string) => {
  try {
    await paymentService.downloadReceipt(paymentId);
  } catch (err) {
    deps.setError(errorMessage(err, 'We couldn’t download this receipt. Check your connection and try again.'));
  }
};

const handleProjectToggle = (deps: HandlerDependencies) => (projectId: string) => {
  const updater = (prev: string[]) => (
    prev.includes(projectId) ? prev.filter((id) => id !== projectId) : [...prev, projectId]
  );

  deps.setSelectedProjectIds(updater);
  deps.setCurrentPage(1);
};

const handleSelectAllProjects = (deps: HandlerDependencies) => () => {
  deps.setSelectedProjectIds(
    deps.selectedProjectIds.length === deps.allProjects.length
      ? []
      : deps.allProjects.map((p) => p.id)
  );
  deps.setCurrentPage(1);
};

const handleClearFilter = (deps: HandlerDependencies) => () => {
  deps.setSelectedProjectIds([]);
  deps.setCurrentPage(1);
};

const toggleProjectExpand = (deps: HandlerDependencies) => (projectId: string) => {
  deps.setExpandedProjects((prev) => {
    const next = new Set(prev);

    if (next.has(projectId)) {
      next.delete(projectId);
    } else {
      next.add(projectId);
    }

    return next;
  });
};

const expandAll = (deps: HandlerDependencies) => () => {
  deps.setExpandedProjects(new Set(deps.projectGroupIds));
};

const collapseAll = (deps: HandlerDependencies) => () => {
  deps.setExpandedProjects(new Set());
};

const handlePageChange = (deps: HandlerDependencies) => (newPage: number) => {
  deps.setCurrentPage(newPage);
};

const handleLedgerPageChange = (deps: HandlerDependencies) => (newPage: number) => {
  deps.setLedgerPage(newPage);
};

export const createHandlers = (deps: HandlerDependencies) => ({
  handleDownloadReceipt: handleDownloadReceipt(deps),
  handleProjectToggle: handleProjectToggle(deps),
  handleSelectAllProjects: handleSelectAllProjects(deps),
  handleClearFilter: handleClearFilter(deps),
  toggleProjectExpand: toggleProjectExpand(deps),
  expandAll: expandAll(deps),
  collapseAll: collapseAll(deps),
  handlePageChange: handlePageChange(deps),
  handleLedgerPageChange: handleLedgerPageChange(deps),
});
